import { getRepoWithHeadOid } from '@mesa/core/repo/queries';
import { fromOid } from '@mesa/vcs/vcs/utils';
import { createServerFn } from '@tanstack/react-start';
import { log } from '../../lib/logger';
import { assertScope } from '../../middleware/base-auth';
import { vcsAuthMiddleware } from '../../middleware/vcs-auth';
import { fetchBranchList, fetchDirContent } from '../vcs';

export const loadRepoDetailPage = createServerFn({ method: 'GET' })
  .middleware([vcsAuthMiddleware])
  .inputValidator((data: { orgSlug: string; name: string; ref?: string }) => data)
  .handler(async ({ data, context }) => {
    assertScope(context.member.role, 'read');
    const orgId = context.org.id;
    const vcs = context.vcs;

    // Step 1: repo + headOid in ONE DB query (no gRPC needed for default branch)
    const { repo, headOid, tags } = await getRepoWithHeadOid(orgId, data.name);

    // Determine which OID to browse
    // data.ref is always a bare branch name (e.g., "main"), set by BranchSelector via URL search params
    let browseOid = headOid;
    if (data.ref && data.ref !== repo.defaultBranch) {
      try {
        const resolved = await vcs.resolveRef(repo.id, data.ref);
        browseOid = resolved?.targetOid ? fromOid(resolved.targetOid) : headOid;
      } catch (err) {
        log.warn('resolve-ref-fallback', { repoId: repo.id, ref: data.ref, error: String(err) });
        browseOid = headOid;
      }
    }

    if (!browseOid) {
      // Empty repo — no commits yet
      return {
        repo,
        tags,
        headOid: null,
        rootContent: null,
        branches: null,
      };
    }

    // Step 2: root content + branch list IN PARALLEL
    const [rootContent, branches] = await Promise.all([
      fetchDirContent(vcs, repo.id, browseOid, ''),
      fetchBranchList(vcs, repo.id, repo.defaultBranch),
    ]);

    return {
      repo,
      tags,
      headOid: browseOid,
      rootContent,
      branches,
    };
  });
